import {
  DeleteWorkflowDefinitionDialog,
  TriggerWorkflowDialog,
  WorkflowDefinitionCategorySection,
  WorkflowDefinitionsEmptyState,
  WorkflowDefinitionsErrorState,
  WorkflowDefinitionsFilterBar,
  WorkflowDefinitionsHeader,
  WorkflowDefinitionsListTable,
  WorkflowDefinitionsLoadingState,
} from '@/components/projects/workflows'
import { useAuth, useDebounce, useDuplicateWorkflowDefinition, useToggleWorkflowDefinitionActive, useWorkflowDefinitions } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import type { WorkflowDefinitionCategory, WorkflowDefinitionFilters, WorkflowDefinitionSummary } from '@/types'
import { WORKFLOW_DEFINITION_CATEGORIES } from '@/types'
import { useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

const CATEGORY_ORDER: WorkflowDefinitionCategory[] = WORKFLOW_DEFINITION_CATEGORIES
const UNCATEGORIZED = 'Uncategorized' as const
type SectionCategory = WorkflowDefinitionCategory | typeof UNCATEGORIZED

const DEFAULT_FILTERS: WorkflowDefinitionFilters = {
  search: '',
  status: 'active',
  type: 'all',
  category: 'all',
}

type ViewMode = 'grid' | 'list'

function readSessionViewMode(projectId: string): ViewMode {
  try {
    const stored = sessionStorage.getItem(`wf-definitions-view-mode-${projectId}`)
    if (stored === 'list' || stored === 'grid') return stored
  } catch { /* ignore */ }
  return 'grid'
}

function readSessionSections(projectId: string): Record<SectionCategory, boolean> {
  const defaults: Record<SectionCategory, boolean> = {
    Analysis: true,
    Implementation: true,
    Testing: true,
    Documentation: true,
    DatabaseMigration: true,
    Uncategorized: true,
  }
  try {
    const stored = sessionStorage.getItem(`wf-definitions-sections-${projectId}`)
    if (stored) {
      const parsed = JSON.parse(stored)
      return { ...defaults, ...parsed }
    }
  } catch { /* ignore */ }
  return defaults
}

export function WorkflowDefinitionsPage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)

  const [filters, setFilters] = useState<WorkflowDefinitionFilters>(DEFAULT_FILTERS)
  const debouncedSearch = useDebounce(filters.search)

  const [viewMode, setViewMode] = useState<ViewMode>(() => readSessionViewMode(projectId ?? ''))
  const [sectionExpanded, setSectionExpanded] = useState<Record<SectionCategory, boolean>>(() =>
    readSessionSections(projectId ?? '')
  )

  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false)
  const [triggerTarget, setTriggerTarget] = useState<WorkflowDefinitionSummary | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<WorkflowDefinitionSummary | null>(null)

  const setFilter = (key: keyof WorkflowDefinitionFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode)
    try { sessionStorage.setItem(`wf-definitions-view-mode-${projectId}`, mode) } catch { /* ignore */ }
  }

  const handleToggleSection = (category: SectionCategory) => {
    setSectionExpanded((prev) => {
      const next = { ...prev, [category]: !prev[category] }
      try { sessionStorage.setItem(`wf-definitions-sections-${projectId}`, JSON.stringify(next)) } catch { /* ignore */ }
      return next
    })
  }

  const { data, isLoading, isError, refetch } = useWorkflowDefinitions(projectId!)
  const allDefinitions = data?.items ?? []

  const duplicateDefinition = useDuplicateWorkflowDefinition(projectId!)
  const toggleActive = useToggleWorkflowDefinitionActive(projectId!)

  const filteredDefinitions = useMemo(() => {
    const search = debouncedSearch.trim().toLowerCase()
    return allDefinitions.filter((def) => {
      if (search && !def.displayName.toLowerCase().includes(search) && !def.description?.toLowerCase().includes(search) && !def.version.toLowerCase().includes(search)) {
        return false
      }
      if (filters.status === 'active' && !def.isActive) return false
      if (filters.status === 'inactive' && def.isActive) return false
      if (filters.type === 'system' && !def.isSystem) return false
      if (filters.type === 'custom' && def.isSystem) return false
      if (filters.category !== 'all' && def.category !== filters.category) return false
      return true
    })
  }, [allDefinitions, debouncedSearch, filters.status, filters.type, filters.category])

  const groupedByCategory = useMemo(() => {
    const map = new Map<SectionCategory, WorkflowDefinitionSummary[]>()
    for (const cat of CATEGORY_ORDER) map.set(cat, [])
    map.set(UNCATEGORIZED, [])
    for (const def of filteredDefinitions) {
      const bucket = def.category && map.has(def.category) ? def.category : UNCATEGORIZED
      map.get(bucket)!.push(def)
    }
    return map
  }, [filteredDefinitions])

  const visibleCategories = useMemo(
    () => CATEGORY_ORDER.filter((cat) => (groupedByCategory.get(cat)?.length ?? 0) > 0),
    [groupedByCategory]
  )

  const uncategorizedDefinitions = groupedByCategory.get(UNCATEGORIZED) ?? []

  if (authLoading) return null

  if (!hasPermission(Permissions.Project.Workflow.View, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  const canDuplicate = hasPermission(Permissions.Project.Workflow.Create, projectId)
  const canToggle = hasPermission(Permissions.Project.Workflow.Edit, projectId)
  const canTrigger = hasPermission(Permissions.Project.Workflow.Trigger, projectId)
  const canDelete = hasPermission(Permissions.Project.Workflow.Delete, projectId)

  const handleNavigate = (def: WorkflowDefinitionSummary) =>
    navigate(`/projects/${projectId}/workflows/definitions/${def.id}`)

  const handleDuplicate = async (def: WorkflowDefinitionSummary) => {
    try {
      const response = await duplicateDefinition.mutateAsync(def.id)
      toast.success(`Duplicated "${def.displayName}"`)
      navigate(`/projects/${projectId}/workflows/definitions/${(response as { workflowDefinitionId: string }).workflowDefinitionId}`)
    } catch {
      toast.error('Failed to duplicate workflow definition')
    }
  }

  const handleToggleActive = async (def: WorkflowDefinitionSummary) => {
    try {
      await toggleActive.mutateAsync(def.id)
      toast.success(`${def.isActive ? 'Disabled' : 'Enabled'} "${def.displayName}"`)
    } catch {
      toast.error('Failed to toggle workflow definition status')
    }
  }

  const handleTrigger = (def: WorkflowDefinitionSummary) => {
    setTriggerTarget(def)
    setTriggerDialogOpen(true)
  }

  const handleDelete = (def: WorkflowDefinitionSummary) => {
    setDeleteTarget(def)
    setDeleteDialogOpen(true)
  }

  const handleCreateDefinition = () =>
    navigate(`/projects/${projectId}/workflows/definitions/new`)

  if (isError) {
    return (
      <div className="space-y-6" data-testid="workflow-definitions-page">
        <WorkflowDefinitionsHeader onCreateDefinition={handleCreateDefinition} />
        <WorkflowDefinitionsErrorState onRetry={() => refetch()} />
      </div>
    )
  }

  const sharedCardProps = {
    canDuplicate,
    canToggle,
    canTrigger,
    canDelete,
    onNavigate: handleNavigate,
    onDuplicate: handleDuplicate,
    onToggleActive: handleToggleActive,
    onTrigger: handleTrigger,
    onDelete: handleDelete,
  }

  const hasAnyDefinitions = allDefinitions.length > 0
  const hasFilteredResults = filteredDefinitions.length > 0

  return (
    <div className="space-y-6" data-testid="workflow-definitions-page">
      <WorkflowDefinitionsHeader onCreateDefinition={handleCreateDefinition} />

      <div className="flex items-center justify-between gap-4">
        <WorkflowDefinitionsFilterBar
          filters={filters}
          onFilterChange={setFilter}
          onClearAll={() => setFilters(DEFAULT_FILTERS)}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />
      </div>

      {isLoading ? (
        <WorkflowDefinitionsLoadingState />
      ) : !hasAnyDefinitions ? (
        <WorkflowDefinitionsEmptyState />
      ) : !hasFilteredResults ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="workflow-definitions-no-results">
          No workflow definitions match your filters.
        </div>
      ) : (
        viewMode === 'list' ? (
          <WorkflowDefinitionsListTable definitions={filteredDefinitions} {...sharedCardProps} />
        ) : (
          <div className="space-y-2" data-testid="workflow-definitions-categories">
            {visibleCategories.map((category) => (
              <WorkflowDefinitionCategorySection
                key={category}
                category={category}
                definitions={groupedByCategory.get(category) ?? []}
                isExpanded={sectionExpanded[category]}
                onToggle={() => handleToggleSection(category)}
                {...sharedCardProps}
              />
            ))}
            {uncategorizedDefinitions.length > 0 && (
              <WorkflowDefinitionCategorySection
                key={UNCATEGORIZED}
                category={UNCATEGORIZED}
                definitions={uncategorizedDefinitions}
                isExpanded={sectionExpanded[UNCATEGORIZED]}
                onToggle={() => handleToggleSection(UNCATEGORIZED)}
                {...sharedCardProps}
              />
            )}
          </div>
        )
      )}

      <TriggerWorkflowDialog
        projectId={projectId!}
        definition={triggerTarget}
        open={triggerDialogOpen}
        onOpenChange={setTriggerDialogOpen}
      />
      <DeleteWorkflowDefinitionDialog
        projectId={projectId!}
        definition={deleteTarget}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      />
    </div>
  )
}
